import { Skeleton } from "@mui/material";
import {
  FiltersSkeleton,
  SummaryCardSkeleton,
} from "../../../../../Components/Molecules/DashboardSkeleton";

const skeletonTone = {
  bgcolor: "var(--skeleton-bg, rgba(0, 0, 0, 0.06))",
};

const skeletonBar = {
  borderRadius: 1,
  transform: "none",
  ...skeletonTone,
};

export const BaasPageHeaderSkeleton = ({
  withAction = false,
  withSubtitle = true,
  large = false,
} = {}) => (
  <header className="min-w-0 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
    <div className="min-w-0 space-y-2">
      <Skeleton variant="text" width={large ? 360 : 280} height={large ? 36 : 32} />
      {withSubtitle ? <Skeleton variant="text" width={320} height={16} /> : null}
    </div>
    {withAction ? (
      <Skeleton variant="rounded" width={140} height={40} sx={{ ...skeletonBar, flexShrink: 0 }} />
    ) : null}
  </header>
);

export const BaasKpiCardsSkeleton = ({ count = 4, withSectionLabel = true } = {}) => (
  <section className="min-w-0 w-full">
    {withSectionLabel ? (
      <Skeleton variant="text" width={120} height={14} sx={{ mb: 3 }} />
    ) : null}
    <div className="dashboard-card-grid">
      {Array.from({ length: count }).map((_, index) => (
        <SummaryCardSkeleton key={index} withFooter={false} />
      ))}
    </div>
  </section>
);

const BaasTableRowSkeleton = ({ columns = 6 }) => (
  <div className="grid grid-cols-2 items-center gap-3 border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border md:grid-cols-[repeat(var(--cols),minmax(0,1fr))]">
    {Array.from({ length: columns }).map((_, index) => (
      <Skeleton
        key={index}
        variant="rounded"
        width={index === 0 ? "85%" : index === columns - 1 ? 72 : "70%"}
        height={index === columns - 1 ? 24 : 14}
        sx={skeletonBar}
      />
    ))}
  </div>
);

export const BaasTableSkeleton = ({ rows = 8, columns = 6 } = {}) => (
  <div
    className="app-card overflow-hidden"
    style={{ "--cols": columns }}
  >
    <div className="hidden gap-3 border-b border-gray-100 bg-gray-50/80 px-4 py-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated/50 md:grid md:grid-cols-[repeat(var(--cols),minmax(0,1fr))]">
      {Array.from({ length: columns }).map((_, index) => (
        <Skeleton key={index} variant="text" height={12} />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, index) => (
      <BaasTableRowSkeleton key={index} columns={columns} />
    ))}
    <div className="flex justify-end px-4 py-3">
      <Skeleton variant="rounded" width={280} height={36} sx={skeletonBar} />
    </div>
  </div>
);

export const BaasQuickActionsSkeleton = ({ count = 4 } = {}) => (
  <div className="flex flex-wrap gap-2">
    {Array.from({ length: count }).map((_, index) => (
      <Skeleton
        key={index}
        variant="rounded"
        width={index === 0 ? 120 : 100}
        height={36}
        sx={{ ...skeletonBar, borderRadius: "9999px" }}
      />
    ))}
  </div>
);

export const BaasDashboardSkeleton = () => (
  <div className="baas-processing min-w-0 space-y-5">
    <BaasPageHeaderSkeleton withAction />

    <Skeleton variant="rounded" height={48} sx={{ ...skeletonBar, width: "100%", maxWidth: 640 }} />

    <BaasKpiCardsSkeleton count={4} />

    <BaasQuickActionsSkeleton count={5} />

    <section className="min-w-0 space-y-3">
      <Skeleton variant="text" width={140} height={14} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} variant="rounded" height={88} sx={skeletonBar} />
        ))}
      </div>
    </section>

    <div className="grid items-stretch gap-5 lg:grid-cols-2">
      <div className="app-card min-h-[16rem] p-4">
        <Skeleton variant="text" width={120} height={16} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={180} sx={{ ...skeletonBar, width: "100%" }} />
      </div>
      <div className="app-card min-h-[16rem] p-4">
        <Skeleton variant="text" width={100} height={16} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={180} sx={{ ...skeletonBar, width: "100%" }} />
      </div>
    </div>

    <div className="app-card overflow-hidden p-4">
      <Skeleton variant="text" width={160} height={16} sx={{ mb: 3 }} />
      {Array.from({ length: 4 }).map((_, index) => (
        <div
          key={index}
          className="flex items-center justify-between border-b border-gray-100 py-3 last:border-0 dark:border-brand-dark-border"
        >
          <div className="flex items-center gap-3">
            <Skeleton variant="circular" width={32} height={32} sx={skeletonTone} />
            <div className="space-y-1.5">
              <Skeleton variant="rounded" width={140} height={14} sx={skeletonBar} />
              <Skeleton variant="rounded" width={80} height={11} sx={skeletonBar} />
            </div>
          </div>
          <Skeleton variant="rounded" width={72} height={14} sx={skeletonBar} />
        </div>
      ))}
    </div>
  </div>
);

export const BaasEntidadesPageSkeleton = () => (
  <div className="baas-entidades min-w-0 space-y-5">
    <BaasPageHeaderSkeleton withAction />
    <Skeleton variant="rounded" height={52} sx={{ ...skeletonBar, width: "100%", maxWidth: 720 }} />
    <BaasKpiCardsSkeleton count={4} />
    <FiltersSkeleton />
    <BaasTableSkeleton rows={8} columns={7} />
  </div>
);

export const BaasTablePageSkeleton = ({ withQuickActions = false, columns = 6 } = {}) => (
  <div className="min-w-0 space-y-5">
    <BaasPageHeaderSkeleton />
    {withQuickActions ? <BaasQuickActionsSkeleton /> : null}
    <FiltersSkeleton />
    <BaasTableSkeleton rows={10} columns={columns} />
  </div>
);

const BaasCardProgramRowSkeleton = () => (
  <div className="rounded-xl border border-gray-100 dark:border-brand-dark-border">
    <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3.5 dark:border-brand-dark-border">
      <div className="min-w-0 space-y-2">
        <Skeleton variant="rounded" width={28} height={20} sx={skeletonBar} />
        <Skeleton variant="rounded" width={180} height={18} sx={skeletonBar} />
        <div className="flex gap-2">
          <Skeleton variant="rounded" width={64} height={18} sx={skeletonBar} />
          <Skeleton variant="rounded" width={56} height={18} sx={skeletonBar} />
        </div>
      </div>
      <Skeleton variant="rounded" width={20} height={20} sx={skeletonBar} />
    </div>
    <div className="grid grid-cols-2 gap-3 px-4 py-3.5 sm:grid-cols-3 lg:grid-cols-5">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="space-y-1.5">
          <Skeleton variant="rounded" width={56} height={10} sx={skeletonBar} />
          <Skeleton variant="rounded" width={80} height={14} sx={skeletonBar} />
        </div>
      ))}
    </div>
  </div>
);

export const BaasCardsListSkeleton = ({
  count = 3,
  withKpis = false,
  withFilters = false,
  kpiCount = 5,
} = {}) => (
  <div className="baas-processing min-w-0 space-y-5">
    <BaasPageHeaderSkeleton withAction />
    {withKpis ? <BaasKpiCardsSkeleton count={kpiCount} /> : null}
    {withFilters ? <FiltersSkeleton /> : null}
    <section className="min-w-0 space-y-3">
      <Skeleton variant="text" width={160} height={14} />
      <div className="flex flex-col gap-3">
        {Array.from({ length: count }).map((_, index) => (
          <BaasCardProgramRowSkeleton key={index} />
        ))}
      </div>
    </section>
  </div>
);

export const BaasApiPageSkeleton = () => (
  <div className="baas-api min-w-0 space-y-5">
    <BaasPageHeaderSkeleton withAction />
    <FiltersSkeleton />
    <BaasKpiCardsSkeleton count={8} />
    <BaasQuickActionsSkeleton count={3} />
    <div className="min-w-0 space-y-4">
      <div className="flex gap-2">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} variant="rounded" width={88} height={32} sx={skeletonBar} />
        ))}
      </div>
      <BaasTableSkeleton rows={5} columns={5} />
    </div>
  </div>
);

const BaasInstanceCardSkeleton = () => (
  <Skeleton
    variant="rounded"
    className="!h-[176px] !w-full !min-w-0"
    sx={skeletonBar}
  />
);

export const BaasInstancesSkeleton = ({ count = 2 } = {}) => (
  <div className="baas-instances min-w-0">
    <header className="mb-8 min-w-0 space-y-2">
      <Skeleton variant="text" width={380} height={36} />
      <Skeleton variant="text" width={420} height={16} />
    </header>
    <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <BaasInstanceCardSkeleton />
      {Array.from({ length: count }).map((_, index) => (
        <BaasInstanceCardSkeleton key={index} />
      ))}
    </div>
  </div>
);

export const BaasGroupsSkeleton = ({ count = 3 } = {}) => (
  <div className="baas-groups min-w-0 space-y-5">
    <BaasPageHeaderSkeleton withAction />
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="app-card p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton variant="rounded" width="70%" height={18} sx={skeletonBar} />
              <Skeleton variant="rounded" width="90%" height={14} sx={skeletonBar} />
            </div>
            <Skeleton variant="rounded" width={72} height={22} sx={skeletonBar} />
          </div>
          <Skeleton variant="rounded" width={80} height={11} sx={{ ...skeletonBar, mt: 4 }} />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Array.from({ length: 3 }).map((__, chipIndex) => (
              <Skeleton key={chipIndex} variant="rounded" width={64} height={22} sx={skeletonBar} />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const BaasSettingsSkeleton = () => (
  <div className="baas-settings min-w-0">
    <header className="mb-6 flex min-w-0 items-start justify-between gap-4">
      <Skeleton variant="text" width={280} height={32} />
      <Skeleton variant="rounded" width={100} height={40} sx={skeletonBar} />
    </header>
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {Array.from({ length: 2 }).map((_, index) => (
        <div key={index} className="app-card space-y-4 p-5">
          <Skeleton variant="text" width={140} height={14} />
          {Array.from({ length: 4 }).map((__, fieldIndex) => (
            <div key={fieldIndex} className="space-y-1.5">
              <Skeleton variant="rounded" width={100} height={12} sx={skeletonBar} />
              <Skeleton variant="rounded" height={40} sx={skeletonBar} />
            </div>
          ))}
        </div>
      ))}
      <div className="app-card space-y-4 p-5 md:col-span-2">
        <Skeleton variant="text" width={120} height={14} />
        <Skeleton variant="rounded" height={120} sx={skeletonBar} />
      </div>
    </div>
  </div>
);

export const BaasCardProgramDetailSkeleton = () => (
  <div className="baas-processing min-w-0 space-y-5">
    <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div className="min-w-0 space-y-2">
        <Skeleton variant="rounded" width={120} height={12} sx={skeletonBar} />
        <Skeleton variant="rounded" width={28} height={24} sx={skeletonBar} />
        <Skeleton variant="text" width={240} height={32} />
        <Skeleton variant="text" width={300} height={14} />
      </div>
      <Skeleton variant="rounded" width={120} height={36} sx={skeletonBar} />
    </header>
    <div className="flex flex-wrap gap-2 border-b border-gray-100 pb-2 dark:border-brand-dark-border">
      {Array.from({ length: 5 }).map((_, index) => (
        <Skeleton key={index} variant="rounded" width={96} height={32} sx={skeletonBar} />
      ))}
    </div>
    <div className="app-card space-y-3 p-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div
          key={index}
          className="flex flex-col gap-1 border-b border-gray-100 py-3 last:border-0 dark:border-brand-dark-border sm:flex-row sm:justify-between"
        >
          <Skeleton variant="rounded" width={140} height={14} sx={skeletonBar} />
          <Skeleton variant="rounded" width={100} height={14} sx={skeletonBar} />
        </div>
      ))}
    </div>
    <BaasKpiCardsSkeleton count={4} />
  </div>
);

export const BaasCreditoPageSkeleton = () => (
  <div className="baas-processing min-w-0 space-y-5">
    <BaasPageHeaderSkeleton />
    <BaasKpiCardsSkeleton count={4} />
  </div>
);

export const BaasAccountDetailSkeleton = () => (
  <div className="baas-account-detail min-w-0 space-y-6">
    <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div className="min-w-0 space-y-2">
        <Skeleton variant="rounded" width={120} height={12} sx={skeletonBar} />
        <div className="flex flex-wrap items-center gap-3">
          <Skeleton variant="text" width={240} height={32} />
          <Skeleton variant="rounded" width={64} height={24} sx={skeletonBar} />
        </div>
        <Skeleton variant="text" width={180} height={14} />
        <Skeleton variant="text" width={200} height={14} />
      </div>
      <Skeleton variant="rounded" width={120} height={36} sx={skeletonBar} />
    </header>
    <BaasQuickActionsSkeleton count={5} />
    <div className="flex flex-wrap gap-2 border-b border-gray-100 pb-2 dark:border-brand-dark-border">
      {Array.from({ length: 2 }).map((_, index) => (
        <Skeleton key={index} variant="rounded" width={96} height={32} sx={skeletonBar} />
      ))}
    </div>
    <BaasKpiCardsSkeleton count={4} />
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="flex flex-col gap-1 border-b border-gray-100 py-3 last:border-0 dark:border-brand-dark-border sm:flex-row sm:justify-between"
          >
            <Skeleton variant="rounded" width={140} height={14} sx={skeletonBar} />
            <Skeleton variant="rounded" width={100} height={14} sx={skeletonBar} />
          </div>
        ))}
      </div>
      <Skeleton variant="rounded" height={180} sx={skeletonBar} />
    </div>
  </div>
);

export const BaasStablesWalletDetailSkeleton = () => (
  <div className="baas-stables-wallet min-w-0 space-y-4">
    <header className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
      <div className="min-w-0 space-y-2">
        <Skeleton variant="rounded" width={120} height={12} sx={skeletonBar} />
        <div className="flex flex-wrap items-center gap-2.5">
          <Skeleton variant="text" width={240} height={32} />
          <Skeleton variant="rounded" width={64} height={24} sx={skeletonBar} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Skeleton variant="rounded" width={72} height={24} sx={skeletonBar} />
          <Skeleton variant="rounded" width={80} height={24} sx={skeletonBar} />
          <Skeleton variant="rounded" width={140} height={28} sx={skeletonBar} />
        </div>
        <Skeleton variant="text" width={200} height={14} />
      </div>
      <Skeleton variant="rounded" width={120} height={36} sx={skeletonBar} />
    </header>
    <BaasQuickActionsSkeleton count={4} />
    <div className="flex flex-wrap gap-2 border-b border-gray-100 pb-2 dark:border-brand-dark-border">
      {Array.from({ length: 2 }).map((_, index) => (
        <Skeleton key={index} variant="rounded" width={96} height={32} sx={skeletonBar} />
      ))}
    </div>
    <BaasKpiCardsSkeleton count={3} />
    <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
      <div className="app-card space-y-2 p-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="flex flex-col gap-1 border-b border-gray-100 py-1.5 last:border-0 dark:border-brand-dark-border sm:flex-row sm:justify-between"
          >
            <Skeleton variant="rounded" width={140} height={14} sx={skeletonBar} />
            <Skeleton variant="rounded" width={100} height={14} sx={skeletonBar} />
          </div>
        ))}
      </div>
      <Skeleton variant="rounded" height={96} sx={skeletonBar} />
    </div>
    <Skeleton variant="rounded" height={40} sx={skeletonBar} />
  </div>
);

export const BaasStablesWalletPageSkeleton = () => (
  <div className="baas-stables-wallet min-w-0 space-y-5">
    <BaasPageHeaderSkeleton />
    <BaasQuickActionsSkeleton count={4} />
    <BaasKpiCardsSkeleton count={4} />
    <div className="app-card p-4">
      <Skeleton variant="text" width={140} height={14} sx={{ mb: 3 }} />
      <div className="grid gap-5 md:grid-cols-2">
        <Skeleton variant="rounded" height={56} sx={skeletonBar} />
        <Skeleton variant="rounded" height={56} sx={skeletonBar} />
      </div>
    </div>
    <FiltersSkeleton />
    <BaasTableSkeleton rows={5} columns={6} />
  </div>
);

/** Body-only skeleton for stables ops pages that already render a real header. */
export const BaasStablesOperationBodySkeleton = () => (
  <div className="min-w-0 space-y-5">
    <BaasKpiCardsSkeleton count={3} />
    <div className="app-card p-4 sm:p-5">
      <Skeleton variant="rounded" height={220} sx={skeletonBar} />
    </div>
    <div className="app-card p-4">
      <Skeleton variant="text" width={160} height={14} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" height={120} sx={skeletonBar} />
    </div>
  </div>
);

export const BaasStablesMetricsPageSkeleton = () => (
  <div className="baas-stables-metrics min-w-0 space-y-5">
    <BaasPageHeaderSkeleton />
    <div className="app-card p-4">
      <Skeleton variant="rounded" height={56} sx={skeletonBar} />
    </div>
    <BaasKpiCardsSkeleton count={4} />
    <div className="app-card p-4">
      <Skeleton variant="text" width={160} height={14} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" height={44} sx={skeletonBar} />
    </div>
    <BaasKpiCardsSkeleton count={4} />
    <div className="grid gap-3 lg:grid-cols-2">
      <div className="app-card p-4">
        <Skeleton variant="text" width={120} height={14} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={120} sx={skeletonBar} />
      </div>
      <div className="app-card p-4">
        <Skeleton variant="text" width={120} height={14} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={120} sx={skeletonBar} />
      </div>
    </div>
  </div>
);
